/**
 * useClientSync.js - SSE Hook for Real-Time Client Portal Updates
 * ================================================================
 * Manages Server-Sent Events connection for receiving real-time
 * updates from admin dashboard actions.
 *
 * Event Types:
 * - balance_update: Credit/adjustment changes
 * - grace_period_update: Grace period extension/activation
 * - dialer_assigned: Admin assigns dialer
 * - dialer_removed: Admin removes dialer
 * - bot_removed: Bot/license removed
 * - bot_setting_changed: Auto-login/revive/voice/transcriber
 * - invoice_approved: Admin approves invoice
 * - invoice_status_changed: Invoice paid/partial/cancelled
 * - request_completed: Bot request processed
 * - dialer_paused: Admin pauses dialer
 * - dialer_resumed: Admin resumes dialer
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE_URL = '/api/client';

/**
 * Hook for SSE-based real-time sync with the admin dashboard
 * @param {Object} options Configuration options
 * @param {string} options.token - JWT token for authentication
 * @param {function} options.onBalanceUpdate - Called when balance changes
 * @param {function} options.onGracePeriodUpdate - Called when grace period changes
 * @param {function} options.onDialerAssigned - Called when dialer is assigned
 * @param {function} options.onDialerRemoved - Called when dialer is removed
 * @param {function} options.onBotRemoved - Called when bot is removed
 * @param {function} options.onBotSettingChanged - Called when bot settings change
 * @param {function} options.onInvoiceApproved - Called when invoice is approved
 * @param {function} options.onInvoiceStatusChanged - Called when invoice status changes
 * @param {function} options.onRequestCompleted - Called when request is completed
 * @param {function} options.onDialerPaused - Called when dialer is paused
 * @param {function} options.onDialerResumed - Called when dialer is resumed
 * @param {function} options.onConnected - Called when SSE connection established
 * @param {function} options.onDisconnected - Called when SSE connection lost
 * @param {boolean} options.enabled - Whether to enable the connection (default: true)
 * @returns {Object} { connected, reconnecting, error, reconnect }
 */
export function useClientSync({
    token,
    onBalanceUpdate,
    onGracePeriodUpdate,
    onDialerAssigned,
    onDialerRemoved,
    onBotRemoved,
    onBotSettingChanged,
    onInvoiceApproved,
    onInvoiceStatusChanged,
    onRequestCompleted,
    onDialerPaused,
    onDialerResumed,
    onConnected,
    onDisconnected,
    enabled = true
}) {
    const [connected, setConnected] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const [error, setError] = useState(null);

    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);

    // Max reconnect delay: 30 seconds
    const MAX_RECONNECT_DELAY = 30000;
    // Base delay: 3 seconds
    const BASE_RECONNECT_DELAY = 3000;

    /**
     * Calculate exponential backoff delay
     */
    const getReconnectDelay = useCallback(() => {
        const delay = Math.min(
            BASE_RECONNECT_DELAY * Math.pow(1.5, reconnectAttemptsRef.current),
            MAX_RECONNECT_DELAY
        );
        return delay;
    }, []);

    /**
     * Connect to SSE endpoint
     */
    const connect = useCallback(() => {
        if (!token || !enabled) return;

        // Close existing connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const url = `${API_BASE_URL}/events?token=${encodeURIComponent(token)}`;

        try {
            const es = new EventSource(url);
            eventSourceRef.current = es;

            // Connection opened
            es.onopen = () => {
                console.log('[ClientSync] SSE connected');
                setConnected(true);
                setReconnecting(false);
                setError(null);
                reconnectAttemptsRef.current = 0;
                onConnected?.();
            };

            // Handle connection established event
            es.addEventListener('connected', (e) => {
                try {
                    const data = JSON.parse(e.data);
                    console.log('[ClientSync] Connected event received:', data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse connected event:', err);
                }
            });

            // Handle balance updates
            es.addEventListener('balance_update', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Balance update:', data);
                    onBalanceUpdate?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse balance_update:', err);
                }
            });

            // Handle grace period updates
            es.addEventListener('grace_period_update', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Grace period update:', data);
                    onGracePeriodUpdate?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse grace_period_update:', err);
                }
            });

            // Handle dialer assigned
            es.addEventListener('dialer_assigned', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Dialer assigned:', data);
                    onDialerAssigned?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse dialer_assigned:', err);
                }
            });

            // Handle dialer removed
            es.addEventListener('dialer_removed', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Dialer removed:', data);
                    onDialerRemoved?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse dialer_removed:', err);
                }
            });

            // Handle bot removed
            es.addEventListener('bot_removed', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Bot removed:', data);
                    onBotRemoved?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse bot_removed:', err);
                }
            });

            // Handle bot setting changed
            es.addEventListener('bot_setting_changed', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Bot setting changed:', data);
                    onBotSettingChanged?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse bot_setting_changed:', err);
                }
            });

            // Handle invoice approved
            es.addEventListener('invoice_approved', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Invoice approved:', data);
                    onInvoiceApproved?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse invoice_approved:', err);
                }
            });

            // Handle invoice status changed
            es.addEventListener('invoice_status_changed', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Invoice status changed:', data);
                    onInvoiceStatusChanged?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse invoice_status_changed:', err);
                }
            });

            // Handle request completed
            es.addEventListener('request_completed', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Request completed:', data);
                    onRequestCompleted?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse request_completed:', err);
                }
            });

            // Handle dialer paused
            es.addEventListener('dialer_paused', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Dialer paused:', data);
                    onDialerPaused?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse dialer_paused:', err);
                }
            });

            // Handle dialer resumed
            es.addEventListener('dialer_resumed', (e) => {
                try {
                    const { data } = JSON.parse(e.data);
                    console.log('[ClientSync] Dialer resumed:', data);
                    onDialerResumed?.(data);
                } catch (err) {
                    console.error('[ClientSync] Failed to parse dialer_resumed:', err);
                }
            });

            // Handle errors
            es.onerror = (err) => {
                console.error('[ClientSync] SSE error:', err);
                setConnected(false);
                setError('Connection lost');
                onDisconnected?.();

                // Auto-reconnect with exponential backoff
                reconnectAttemptsRef.current++;
                const delay = getReconnectDelay();

                console.log(`[ClientSync] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
                setReconnecting(true);

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, delay);
            };

        } catch (err) {
            console.error('[ClientSync] Failed to create EventSource:', err);
            setError(err.message);
        }
    }, [
        token, enabled, getReconnectDelay,
        onConnected, onDisconnected,
        onBalanceUpdate, onGracePeriodUpdate,
        onDialerAssigned, onDialerRemoved,
        onBotRemoved, onBotSettingChanged,
        onInvoiceApproved, onInvoiceStatusChanged,
        onRequestCompleted, onDialerPaused, onDialerResumed
    ]);

    /**
     * Manual reconnect function
     */
    const reconnect = useCallback(() => {
        reconnectAttemptsRef.current = 0;
        connect();
    }, [connect]);

    /**
     * Disconnect and cleanup
     */
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        setConnected(false);
        setReconnecting(false);
    }, []);

    // Connect when token changes or enabled state changes
    useEffect(() => {
        if (token && enabled) {
            connect();
        } else {
            disconnect();
        }

        return () => {
            disconnect();
        };
    }, [token, enabled, connect, disconnect]);

    return {
        connected,
        reconnecting,
        error,
        reconnect
    };
}

export default useClientSync;
